import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert,
  Tabs, Tab, Stepper, Step, StepLabel,
  TextField, FormControl, Select, MenuItem,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ReplayIcon from '@mui/icons-material/Replay';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddIcon from '@mui/icons-material/Add';
import useTurnoForm, { FORM_VACIO } from './useTurnoForm';
import { primerNombrePaciente, nombreNaturalPaciente } from '../../utils/nombrePaciente';
import AgendaCalendar from './AgendaCalendar';
import TabDatos from './TabDatos';
import TabCoseguro from './TabCoseguro';
import CrearPacienteDialog from './CrearPacienteDialog';
import DialogShell from '../design-system/DialogShell';
import { fieldSx, labelSx, menuProps, btnPrimary, btnDestructive } from '../../styles/formStyles';

function LF({ label, children }) {
  return (
    <Box>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

const ESTADOS = [
  { value: 'pendiente',    label: 'Pendiente',    dot: '#90a4ae' },
  { value: 'confirmado',   label: 'Confirmado',   dot: '#42a5f5' },
  { value: 'en_recepcion', label: 'En recepción', dot: '#66bb6a' },
  { value: 'demorado',     label: 'Demorado',     dot: '#ffa726' },
  { value: 'atendido',     label: 'Atendido',     dot: '#26a69a' },
  { value: 'cancelado',    label: 'Cancelado',    dot: '#ef5350' },
];

export default function TurnoDialog({
  open,
  onClose,
  onSuccess,
  turnoId = null,
  turno = null,
  disponibilidades = {},
  medicos = [],
  obrasSociales = [],
  initialValues = null,
  modoRecibir = false,
  origen = 'TurnoDialog',
}) {
  const navigate = useNavigate();
  const rolActual = sessionStorage.getItem('rol') || '';

  const medicoIdSesion = sessionStorage.getItem('medico_id') || '';

  const [activeTab, setActiveTab] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [confirmarEliminar, setConfirmarEliminar] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [showCrearPaciente, setShowCrearPaciente] = useState(false);
  const [slotsDialogo, setSlotsDialogo] = useState([]);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [turnoGuardado, setTurnoGuardado] = useState(null);
  const [emailModalValue, setEmailModalValue] = useState('');
  const [estadoEmail, setEstadoEmail] = useState(null); // { estado: 'pendiente'|'enviado'|'error'|'sin_envio', destinatario, mensaje }
  const [confirmandoReenvio, setConfirmandoReenvio] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  const handleSuccess = (turnoData) => {
    if (!modoEdicion) {
      // Nuevo turno creado: mostrar pantalla de éxito dentro del diálogo
      const medicoSel = medicos.find(m => m.id === form.medico_id);
      setTurnoGuardado({
        id: turnoData?.data?.id,
        medico: medicoSel,
        paciente_nombre: form.paciente_nombre,
        paciente_telefono: form.paciente_telefono,
        paciente_email: form.paciente_email,
        fecha: form.fecha,
        hora_inicio: form.hora_inicio,
      });
      onSuccess?.(); // refrescar lista del padre
      return;
    }
    if (!enProcesoRecepcion) {
      // Modo Edición: el paciente sigue siendo el mismo turno puntual a corregir, no hay
      // proceso que acompañar — un guardado alcanza.
      onClose();
      onSuccess?.();
      return;
    }
    // Modo Proceso de Recepción: el diálogo nunca se cierra solo. Paso 1 (Datos del turno,
    // que ya incluye todos los datos del paciente) avanza directo a Coseguro; y vuelve a
    // avanzar si el usuario retrocedió a corregir algo. Recién cuando el coseguro quedó
    // resuelto en el paso 2, el proceso terminó y puede cerrarse.
    if (activeTab === 0) {
      setActiveTab(1);
    } else if (form.coseguro_estado) {
      onClose();
    }
    onSuccess?.(); // refrescar lista sin cerrar (o antes de cerrar)
  };

  const {
    form, setForm,
    guardando, eliminando,
    buscandoDni, estadoDni, setEstadoDni,
    error, setError,
    puedeEditarCoseguro, esAdmin,
    confirmarSobreturno, setConfirmarSobreturno,
    confirmarFueraDeAgenda, setConfirmarFueraDeAgenda,
    requiereEmail, setRequiereEmail,
    resetForm, cargarTurno,
    buscarPorDni, poblarPacienteDesdeCreacion,
    guardar, enviarTurno, confirmarEmailYContinuar,
    confirmarYGuardarSobreturno, confirmarYGuardarFueraDeAgenda,
    cancelarTurno,
    eliminar,
    ejecutarCoseguro, corregirCoseguro, anularCoseguro,
    duracionAgenda,
  } = useTurnoForm({ turnoId, onSuccess: handleSuccess, disponibilidades, origen });

  // Variables derivadas — todas juntas, antes de cualquier useEffect
  const modoEdicion = Boolean(turnoId);
  // El diálogo tiene dos modos de funcionamiento, no un estado más a chequear:
  //  - Modo Edición: se corrige algo puntual de un turno (nota, teléfono, médico). Guardar cierra.
  //  - Modo Proceso de Recepción: el paciente está siendo recibido. Guardar nunca cierra por sí
  //    solo — el diálogo acompaña Datos → Recepción → Coseguro hasta que el proceso está
  //    completo (coseguro resuelto), recién ahí cierra. Se deriva de form.estado en vez de
  //    depender de por qué puerta se entró (antes solo pasaba esto viniendo de "En recepción").
  const enProcesoRecepcion = form.estado === 'en_recepcion';
  const mostrarRecepcionCoseguro = ['en_recepcion', 'atendido'].includes(form.estado);
  const readOnly = form.estado === 'atendido';
  const puedeCancel = modoEdicion && !['cancelado', 'atendido'].includes(form.estado);

  useEffect(() => {
    if (open) {
      if (turnoId && turno) { cargarTurno(turno); }
      else {
        const base = initialValues ? { ...FORM_VACIO, ...initialValues } : undefined;
        resetForm(base);
        // Preseleccionar médico para rol médico
        if (rolActual === 'medico' && medicoIdSesion) {
          setForm(f => ({ ...f, medico_id: Number(medicoIdSesion) }));
        }
      }

      if (modoRecibir) {
        setForm(f => ({ ...f, estado: 'en_recepcion' }));
      }
      setActiveTab(0);
      setActiveStep(0);
    } else {
      resetForm();
      setSlotsDialogo([]);
      setTurnoGuardado(null);
      setEstadoEmail(null);
      setConfirmandoReenvio(false);
    }
  }, [open, turnoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const API = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (requiereEmail) setEmailModalValue(form.paciente_email || '');
  }, [requiereEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // El envío del comprobante es automático al crear el turno — acá no se "manda" nada,
  // solo se consulta qué pasó con ese envío. Mientras esté 'pendiente' se reconsulta cada
  // pocos segundos, para que el recepcionista vea el resultado sin tener que hacer nada.
  async function consultarEstadoEmail(id) {
    try {
      const token = sessionStorage.getItem('token') || '';
      const r = await fetch(`${API}/api/turnos/${id}/estado-email`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      setEstadoEmail(d?.data || { estado: 'sin_envio' });
    } catch {
      setEstadoEmail({ estado: 'sin_envio' });
    }
  }

  useEffect(() => {
    if (!turnoGuardado?.id) return;
    consultarEstadoEmail(turnoGuardado.id);
    const intervalo = setInterval(() => {
      setEstadoEmail(actual => {
        if (actual?.estado === 'pendiente' || actual === null) consultarEstadoEmail(turnoGuardado.id);
        return actual;
      });
    }, 3000);
    return () => clearInterval(intervalo);
  }, [turnoGuardado]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reenviarComprobante() {
    if (!turnoGuardado?.id) return;
    setReenviando(true);
    setConfirmandoReenvio(false);
    try {
      const token = sessionStorage.getItem('token') || '';
      const r = await fetch(`${API}/api/turnos/${turnoGuardado.id}/enviar-comprobante-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'No se pudo reenviar el comprobante.');
      setEstadoEmail({ estado: 'pendiente' });
      setTimeout(() => consultarEstadoEmail(turnoGuardado.id), 2000);
    } catch (err) {
      setEstadoEmail({ estado: 'error', mensaje: err.message });
    } finally {
      setReenviando(false);
    }
  }

  function fmtHaceRato(iso) {
    if (!iso) return '';
    const segundos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (segundos < 60) return 'Hace unos segundos.';
    const minutos = Math.round(segundos / 60);
    return `Hace ${minutos} minuto${minutos === 1 ? '' : 's'}.`;
  }

  // La duración por defecto de un turno nuevo tiene que reflejar el tamaño de slot
  // real configurado para ese médico ese día (15 min, 20 min, etc.), no quedarse
  // siempre en el default genérico de 30. Solo aplica al crear -- en edición la
  // duración ya guardada del turno no se toca.
  useEffect(() => {
    if (!open || modoEdicion || !form.medico_id || !form.fecha) return;
    const duracionReal = duracionAgenda(form.medico_id, form.fecha);
    setForm(prev => ({ ...prev, duracion: String(duracionReal) }));
  }, [open, modoEdicion, form.medico_id, form.fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch slots when medico_id + fecha change (BUG 2)
  useEffect(() => {
    if (!open || !form.medico_id || !form.fecha) { setSlotsDialogo([]); return; }
    let cancelled = false;
    setCargandoSlots(true);
    const token = sessionStorage.getItem('token') || '';
    fetch(`${API}/api/medicos/${form.medico_id}/slots?fecha=${form.fecha}&todos=1`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : {})
      .then(data => { if (!cancelled) setSlotsDialogo(Array.isArray(data.slots) ? data.slots : []); })
      .catch(() => { if (!cancelled) setSlotsDialogo([]); })
      .finally(() => { if (!cancelled) setCargandoSlots(false); });
    return () => { cancelled = true; };
  }, [open, form.medico_id, form.fecha]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mostrarRecepcionCoseguro && activeTab > 0) setActiveTab(0);
  }, [mostrarRecepcionCoseguro]); // eslint-disable-line react-hooks/exhaustive-deps

  const f = (field) => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  /* ─── Stepper step 0: Médico + fecha/hora ─── */
  function renderStep0() {
    const esMedico = rolActual === 'medico';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <LF label="Médico">
          <FormControl fullWidth sx={fieldSx} disabled={esMedico}>
            <Select displayEmpty value={form.medico_id} onChange={f('medico_id')}
              sx={{ color: 'var(--c-texto)' }} MenuProps={menuProps}>
              <MenuItem value=""><em style={{ color: '#888' }}>Seleccioná un médico</em></MenuItem>
              {medicos.map(m => (
                <MenuItem key={m.id} value={m.id}>
                  {m.nombre}{m.especialidad ? ` — ${m.especialidad}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </LF>

        {/* Calendario de agenda */}
        <AgendaCalendar
          medicoId={form.medico_id}
          selectedDate={form.fecha}
          onSelectDate={fecha => setForm(prev => ({ ...prev, fecha }))}
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
          <LF label="Fecha">
            <TextField type="date" fullWidth value={form.fecha} onChange={f('fecha')}
              inputProps={{ style: { colorScheme: 'dark' } }} sx={fieldSx} />
          </LF>
          <LF label="Hora">
            <TextField type="time" fullWidth value={form.hora_inicio} onChange={f('hora_inicio')}
              inputProps={{ style: { colorScheme: 'dark' } }} sx={fieldSx} />
          </LF>
          <LF label="Duración (min)">
            <TextField type="number" fullWidth value={form.duracion} onChange={f('duracion')} sx={fieldSx} />
          </LF>
        </Box>

        {/* Slots disponibles — aparecen al seleccionar médico + fecha */}
        {form.medico_id && form.fecha && (
          <Box>
            <Typography sx={{ fontSize: 11, color: '#90a4ae', mb: 0.5 }}>
              {cargandoSlots ? 'Cargando disponibilidad…' : 'Horarios disponibles — hacé clic para seleccionar'}
            </Typography>
            {!cargandoSlots && slotsDialogo.length === 0 && (
              <Typography sx={{ fontSize: 11, color: '#666' }}>Sin agenda configurada para esta fecha.</Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {slotsDialogo.map((slot, i) => {
                const hora = slot.hora || '';
                const ocupado = slot.ocupado;
                return (
                  <Box
                    key={i}
                    onClick={() => { if (!ocupado) setForm(prev => ({ ...prev, hora_inicio: hora })); }}
                    sx={{
                      px: 1, py: 0.25, borderRadius: 1, fontSize: 12, fontWeight: 500,
                      cursor: ocupado ? 'default' : 'pointer',
                      bgcolor: ocupado ? 'rgba(239,83,80,0.15)' : 'rgba(102,187,106,0.15)',
                      color: ocupado ? '#ef5350' : '#66bb6a',
                      border: form.hora_inicio === hora && !ocupado
                        ? '1px solid #66bb6a'
                        : '1px solid transparent',
                      '&:hover': ocupado ? {} : { bgcolor: 'rgba(102,187,106,0.3)' },
                      textDecoration: ocupado ? 'line-through' : 'none',
                    }}
                  >
                    {hora.substring(0, 5)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  /* ─── Stepper step 1: Datos del paciente ─── */
  function renderStep1() {
    // Si el DNI no corresponde a ningún paciente, el resto del formulario no tiene
    // sentido hasta que se lo cree -- lo que se tipee ahí se perdería igual, así que
    // se deshabilita para que quede claro que el paso es "Crear paciente" primero.
    const sinPaciente = estadoDni === 'no_encontrado';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box>
          <Typography sx={labelSx}>DNI</Typography>
          <Box sx={{ position: 'relative' }}>
            <TextField fullWidth value={form.paciente_dni}
              onChange={e => { setForm(prev => ({ ...prev, paciente_dni: e.target.value })); setEstadoDni(null); }}
              onBlur={() => buscarPorDni(form.paciente_dni)}
              inputProps={{ style: { paddingRight: 32 } }} sx={fieldSx} />
            {buscandoDni && <CircularProgress size={16} sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#90a4ae' }} />}
            {!buscandoDni && estadoDni === 'encontrado' && <CheckCircleIcon sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#66bb6a' }} />}
            {!buscandoDni && estadoDni === 'no_encontrado' && <ErrorIcon sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 18, color: '#ef5350' }} />}
          </Box>
          {!buscandoDni && estadoDni === 'no_encontrado' && (
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#ef9a9a' }}>Paciente no encontrado.</Typography>
              <Button size="small" onClick={() => setShowCrearPaciente(true)}
                sx={{ fontSize: 11, color: '#81c784', p: 0, minWidth: 0, textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                Crear paciente →
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <LF label="Nombre completo">
            <TextField fullWidth value={form.paciente_nombre} onChange={f('paciente_nombre')} disabled={sinPaciente} sx={fieldSx} />
          </LF>
          <LF label="Teléfono">
            <TextField fullWidth value={form.paciente_telefono} onChange={f('paciente_telefono')} disabled={sinPaciente} sx={fieldSx} />
          </LF>
          <LF label="Email">
            <TextField fullWidth value={form.paciente_email} onChange={f('paciente_email')} disabled={sinPaciente} sx={fieldSx} />
          </LF>
          <LF label="Motivo">
            <TextField fullWidth value={form.motivo} onChange={f('motivo')} disabled={sinPaciente} sx={fieldSx} />
          </LF>
        </Box>
        {!sinPaciente && !form.paciente_email?.trim() && (
          <Typography sx={{ fontSize: 11, color: '#ffb74d', mt: -0.5 }}>
            ⚠️ Este paciente no tiene correo electrónico registrado.
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <LF label="Obra social">
            <FormControl fullWidth sx={fieldSx} disabled={sinPaciente}>
              <Select displayEmpty value={form.paciente_obra_social} onChange={f('paciente_obra_social')}
                sx={{ color: '#e0e0e0' }} MenuProps={menuProps}>
                <MenuItem value=""><em style={{ color: '#888' }}>Sin obra social</em></MenuItem>
                {obrasSociales.map(os => (
                  <MenuItem key={os.id ?? os} value={os.nombre ?? os}>{os.nombre ?? os}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </LF>
          <LF label="Número de afiliado">
            <TextField fullWidth value={form.paciente_numero_afiliado} onChange={f('paciente_numero_afiliado')} disabled={sinPaciente} sx={fieldSx} />
          </LF>
        </Box>
        <LF label="Estado">
          <TextField fullWidth value="Pendiente" disabled sx={fieldSx} />
        </LF>
        <LF label="Notas internas">
          <TextField fullWidth value={form.notas} onChange={f('notas')} disabled={sinPaciente} sx={fieldSx} />
        </LF>
      </Box>
    );
  }

  /* ─── Stepper step 2: Confirmar ─── */
  function renderStep2() {
    const medicoSeleccionado = medicos.find(m => m.id === form.medico_id);
    const rows = [
      { label: 'Médico', value: medicoSeleccionado ? medicoSeleccionado.nombre : '—' },
      { label: 'Fecha', value: form.fecha || '—' },
      { label: 'Hora', value: form.hora_inicio || '—' },
      { label: 'Duración', value: form.duracion ? `${form.duracion} min` : '—' },
      { label: 'Paciente', value: form.paciente_nombre || '—' },
      { label: 'DNI', value: form.paciente_dni || '—' },
      { label: 'Motivo', value: form.motivo || '—' },
      { label: 'Estado', value: ESTADOS.find(e => e.value === form.estado)?.label || '—' },
    ].filter(r => r.value !== '—');

    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#0d1421', border: '1px solid #1e2d44' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: 0.8, mb: 2 }}>
          Resumen del turno
        </Typography>
        {rows.map(r => (
          <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#546e7a' }}>{r.label}</Typography>
            <Typography sx={{ fontSize: 13, color: '#e0e0e0', fontWeight: 500 }}>{r.value}</Typography>
          </Box>
        ))}
      </Box>
    );
  }

  /* ─── Helpers para pantalla de éxito ─── */
  function formatFechaLarga(fechaStr) {
    if (!fechaStr) return '—';
    const DIAS  = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
    const MESES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
    const [year, month, day] = fechaStr.split('-').map(Number);
    const d = new Date(year, month - 1, day, 12);
    return `${DIAS[d.getDay()]} ${day} de ${MESES[month - 1]}`;
  }

  async function imprimirComprobante() {
    const { id } = turnoGuardado;
    try {
      const token = sessionStorage.getItem('token') || '';
      const r = await fetch(`${API}/api/turnos/${id}/comprobante.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('No se pudo generar el comprobante.');
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error al abrir comprobante:', err);
    }
  }

  /* ─── Pantalla de éxito post-creación (R-UX-02 / R-UX-07) ─── */
  function renderSuccess() {
    const { medico, paciente_nombre, paciente_telefono, paciente_email, fecha, hora_inicio } = turnoGuardado;
    const nombreMedico = medico?.nombre       || '—';
    const especialidad = medico?.especialidad || '';
    const fechaLarga   = formatFechaLarga(fecha);
    const fechaDisplay = fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1);
    const primerNombre = primerNombrePaciente(paciente_nombre);
    const telLimpio    = (paciente_telefono || '').replace(/\D/g, '');

    const msgWa = encodeURIComponent(
      `Hola ${primerNombre}.\nSu turno fue confirmado.\n📅 ${fechaDisplay}\n🕘 ${hora_inicio}\n👨‍⚕️ ${nombreMedico}${especialidad ? `\nEspecialidad: ${especialidad}` : ''}\nLo esperamos 10 minutos antes del horario asignado.`
    );
    const waUrl = telLimpio ? `https://wa.me/549${telLimpio}?text=${msgWa}` : null;

    const filasSummary = [
      { label: 'Paciente',    value: nombreNaturalPaciente(paciente_nombre) },
      { label: 'Profesional', value: nombreMedico },
      { label: 'Especialidad',value: especialidad },
      { label: 'Fecha',       value: fechaDisplay },
      { label: 'Hora',        value: hora_inicio },
    ].filter(r => r.value);

    return (
      <Box sx={{ py: 1 }}>
        {/* Encabezado */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: '#66bb6a', mb: 1 }} />
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#e0e0e0' }}>
            Turno registrado
          </Typography>
        </Box>

        {/* Resumen del turno */}
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#080e1a', border: '1px solid #1a2640', mb: 3 }}>
          {filasSummary.map(r => (
            <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography sx={{ fontSize: 12, color: '#546e7a' }}>{r.label}</Typography>
              <Typography sx={{ fontSize: 13, color: '#e0e0e0', fontWeight: 500 }}>{r.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 300, mx: 'auto' }}>
          <Button variant="outlined" fullWidth startIcon={<PrintIcon />}
            onClick={imprimirComprobante}
            sx={{ color: '#90a4ae', borderColor: 'rgba(144,164,174,0.25)', textTransform: 'none',
              '&:hover': { borderColor: '#90a4ae', bgcolor: 'rgba(144,164,174,0.05)' } }}>
            Imprimir comprobante
          </Button>

          <Button variant="outlined" fullWidth startIcon={<WhatsAppIcon />}
            component={waUrl ? 'a' : 'button'}
            href={waUrl || undefined}
            target={waUrl ? '_blank' : undefined}
            rel={waUrl ? 'noopener noreferrer' : undefined}
            disabled={!waUrl}
            title={!waUrl ? 'El paciente no posee un teléfono registrado' : ''}
            sx={{ color: '#66bb6a', borderColor: 'rgba(102,187,106,0.3)', textTransform: 'none',
              '&:hover': { borderColor: '#66bb6a', bgcolor: 'rgba(102,187,106,0.05)' },
              '&.Mui-disabled': { color: 'rgba(102,187,106,0.3)', borderColor: 'rgba(102,187,106,0.15)' } }}>
            Enviar por WhatsApp
          </Button>

          {!paciente_email ? (
            <Typography sx={{ fontSize: 11.5, color: '#78909c', textAlign: 'center' }}>
              El paciente no tiene email registrado.
            </Typography>
          ) : (
            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#080e1a', border: '1px solid #1a2640' }}>
              {(!estadoEmail || estadoEmail.estado === 'pendiente') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HourglassEmptyIcon sx={{ fontSize: 18, color: '#ffa726' }} />
                  <Box>
                    <Typography sx={{ fontSize: 12.5, color: '#ffa726', fontWeight: 600 }}>
                      El comprobante está pendiente de envío.
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#78909c' }}>
                      Se enviará automáticamente en unos segundos.
                    </Typography>
                  </Box>
                </Box>
              )}

              {estadoEmail?.estado === 'enviado' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: '#66bb6a' }} />
                    <Typography sx={{ fontSize: 12.5, color: '#66bb6a', fontWeight: 600 }}>
                      Comprobante enviado por email
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11.5, color: '#90a4ae', mb: 0.25 }}>
                    Enviado a: {estadoEmail.destinatario}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: '#546e7a', mb: 1 }}>
                    {fmtHaceRato(estadoEmail.enviado_at)}
                  </Typography>

                  {!confirmandoReenvio ? (
                    <Button size="small" startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
                      onClick={() => setConfirmandoReenvio(true)}
                      sx={{ fontSize: 11.5, color: '#78909c', textTransform: 'none', p: 0,
                        '&:hover': { color: '#90a4ae', bgcolor: 'transparent' } }}>
                      Reenviar comprobante
                    </Button>
                  ) : (
                    <Box>
                      <Typography sx={{ fontSize: 11.5, color: '#cfd8dc', mb: 0.75 }}>
                        ¿Reenviar comprobante a {estadoEmail.destinatario}?
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small" onClick={() => setConfirmandoReenvio(false)}
                          sx={{ fontSize: 11.5, color: '#78909c', textTransform: 'none' }}>
                          Cancelar
                        </Button>
                        <Button size="small" variant="outlined" disabled={reenviando} onClick={reenviarComprobante}
                          sx={{ fontSize: 11.5, textTransform: 'none', color: '#64b5f6', borderColor: 'rgba(100,181,246,0.3)' }}>
                          {reenviando ? 'Reenviando...' : 'Reenviar'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {estadoEmail?.estado === 'error' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <ErrorIcon sx={{ fontSize: 18, color: '#ef5350' }} />
                    <Typography sx={{ fontSize: 12.5, color: '#ef5350', fontWeight: 600 }}>
                      No se pudo enviar el comprobante.
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#90a4ae', mb: 1 }}>
                    Motivo: {estadoEmail.mensaje}
                  </Typography>
                  <Button size="small" variant="outlined" fullWidth disabled={reenviando} onClick={reenviarComprobante}
                    sx={{ fontSize: 11.5, textTransform: 'none', color: '#ef5350', borderColor: 'rgba(239,83,80,0.3)' }}>
                    {reenviando ? 'Reintentando...' : 'Reintentar envío'}
                  </Button>
                </>
              )}

              {estadoEmail?.estado === 'sin_envio' && (
                <Typography sx={{ fontSize: 11.5, color: '#78909c' }}>
                  Todavía no se generó ningún envío para este turno.
                </Typography>
              )}
            </Box>
          )}

          <Button variant="outlined" fullWidth startIcon={<AddIcon />}
            onClick={() => { setTurnoGuardado(null); resetForm(); setActiveStep(0); }}
            sx={{ color: 'var(--c-acento)', borderColor: 'rgba(30,86,160,0.35)', textTransform: 'none',
              '&:hover': { borderColor: 'var(--c-acento)', bgcolor: 'rgba(30,86,160,0.06)' } }}>
            Crear otro turno
          </Button>
        </Box>
      </Box>
    );
  }

  /* ─── Actions para DialogShell ─── */
  function getActionsStart() {
    if (turnoGuardado) return null;
    if (!modoEdicion) {
      return <Button onClick={onClose} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>;
    }
    if (rolActual === 'medico') {
      const listoParaAtender = ['en_recepcion', 'demorado'].includes(form.estado);
      return (
        <Button startIcon={<MedicalServicesIcon />} variant="outlined"
          onClick={() => { onClose(); navigate(`/consulta/${turnoId}`); }}
          disabled={!listoParaAtender}
          title={!listoParaAtender ? 'El turno debe estar en recepción para ser atendido' : ''}
          sx={{ color: '#81c784', borderColor: '#81c784', '&:hover': { borderColor: '#a5d6a7', bgcolor: 'rgba(76,175,80,0.08)' },
                '&.Mui-disabled': { color: 'rgba(129,199,132,0.35)', borderColor: 'rgba(129,199,132,0.2)' } }}>
          Atender
        </Button>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        {puedeCancel && (
          <Button variant="outlined" onClick={() => setConfirmarCancelar(true)}
            sx={{ color: '#ef9a9a', borderColor: 'rgba(239,154,154,0.4)', '&:hover': { borderColor: '#ef9a9a', bgcolor: 'rgba(239,154,154,0.06)' } }}>
            Cancelar turno
          </Button>
        )}
        {esAdmin && (
          <Button onClick={() => setConfirmarEliminar(true)} disabled={eliminando} sx={btnDestructive}>
            {eliminando ? 'Eliminando...' : 'Eliminar'}
          </Button>
        )}
      </Box>
    );
  }

  function getActions() {
    if (turnoGuardado) {
      return (
        <Button onClick={() => { setTurnoGuardado(null); onClose(); }} sx={{ color: 'var(--c-acento)' }}>
          Cerrar
        </Button>
      );
    }
    if (modoEdicion) {
      // En modo Proceso de Recepción, el botón acompaña el paso en el que está el usuario:
      // "Guardar y continuar" mientras falten pasos, "Finalizar" recién cuando el coseguro
      // ya quedó resuelto (Cobrado/Exento) en la pestaña Coseguro.
      const enPasoFinal = enProcesoRecepcion && activeTab === 1;
      const labelGuardar = !enProcesoRecepcion ? 'Guardar' : enPasoFinal ? 'Finalizar' : 'Guardar y continuar';
      const deshabilitado = guardando || (enPasoFinal && !form.coseguro_estado);
      return (
        <>
          <Button onClick={onClose} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button variant="contained" onClick={() => guardar()}
            disabled={deshabilitado}
            title={enPasoFinal && !form.coseguro_estado ? 'Resolvé el coseguro (Cobrar o Exento) antes de finalizar' : ''}
            sx={btnPrimary}>
            {guardando ? 'Guardando...' : labelGuardar}
          </Button>
        </>
      );
    }
    return (
      <>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(s => s - 1)} sx={{ color: 'var(--c-acento)' }}>Atrás</Button>
        )}
        {activeStep < 2 ? (
          <Button variant="contained" onClick={() => {
            if (activeStep === 0 && (!form.medico_id || !form.fecha || !form.hora_inicio)) {
              setError('Médico, fecha y hora son obligatorios.'); return;
            }
            if (activeStep === 1 && !form.paciente_nombre?.trim()) {
              setError('El nombre del paciente es obligatorio.'); return;
            }
            setError(''); setActiveStep(s => s + 1);
          }} disabled={activeStep === 1 && estadoDni === 'no_encontrado'} sx={btnPrimary}>
            Siguiente
          </Button>
        ) : (
          <Button variant="contained" onClick={() => guardar()} disabled={guardando} sx={btnPrimary}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </>
    );
  }

  return (
    <>
      {/* ── Confirmación sobreturno ── */}
      <DialogShell
        open={confirmarSobreturno}
        onClose={() => setConfirmarSobreturno(false)}
        maxWidth="xs"
        title="⚡ Horario ya ocupado"
        actions={
          <>
            <Button onClick={() => setConfirmarSobreturno(false)} sx={{ color: '#90a4ae' }}>Cancelar</Button>
            <Button variant="contained" onClick={confirmarYGuardarSobreturno}
              sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
              Sí, sobreturno
            </Button>
          </>
        }
      >
        <Typography sx={{ color: 'var(--c-texto)', fontSize: 14 }}>
          Ya existe un turno en ese horario. ¿Querés registrar un sobreturno de todas formas?
        </Typography>
      </DialogShell>

      {/* ── Confirmación fuera de agenda ── */}
      <DialogShell
        open={confirmarFueraDeAgenda}
        onClose={() => setConfirmarFueraDeAgenda(false)}
        maxWidth="xs"
        title="📅 Fuera de agenda"
        actions={
          <>
            <Button onClick={() => setConfirmarFueraDeAgenda(false)} sx={{ color: '#90a4ae' }}>Cancelar</Button>
            <Button variant="contained" onClick={confirmarYGuardarFueraDeAgenda}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
              Sí, guardar igual
            </Button>
          </>
        }
      >
        <Typography sx={{ color: 'var(--c-texto)', fontSize: 14 }}>
          El médico no tiene agenda configurada para esa fecha/hora. ¿Guardás igual como sobreturno?
        </Typography>
      </DialogShell>

      {/* ── Email obligatorio para confirmar ── */}
      <DialogShell
        open={requiereEmail}
        onClose={() => setRequiereEmail(false)}
        maxWidth="xs"
        title="📧 Falta el email del paciente"
        actions={
          <>
            <Button onClick={() => setRequiereEmail(false)} sx={{ color: '#90a4ae' }}>Cancelar</Button>
            <Button variant="contained" disabled={guardando || !emailModalValue.trim()}
              onClick={() => confirmarEmailYContinuar(emailModalValue.trim())}
              sx={btnPrimary}>
              {guardando ? 'Guardando...' : 'Guardar y confirmar'}
            </Button>
          </>
        }
      >
        <Typography sx={{ color: 'var(--c-texto)', fontSize: 14, mb: 2 }}>
          El paciente no tiene un correo electrónico registrado. Para confirmar el turno hace falta un email — ahí se le envía el comprobante automáticamente.
        </Typography>
        <TextField fullWidth autoFocus type="email" placeholder="email@ejemplo.com"
          value={emailModalValue} onChange={e => setEmailModalValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && emailModalValue.trim()) confirmarEmailYContinuar(emailModalValue.trim()); }}
          sx={fieldSx} />
      </DialogShell>

      {/* ── Confirmación cancelar turno ── */}
      <DialogShell
        open={confirmarCancelar}
        onClose={() => { setConfirmarCancelar(false); setMotivoCancelacion(''); }}
        maxWidth="xs"
        title="Cancelar turno"
        actions={
          <>
            <Button onClick={() => { setConfirmarCancelar(false); setMotivoCancelacion(''); }} sx={{ color: '#90a4ae' }}>Volver</Button>
            <Button variant="contained"
              onClick={() => { setConfirmarCancelar(false); cancelarTurno(motivoCancelacion || null); setMotivoCancelacion(''); }}
              sx={{ bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }}>
              Confirmar cancelación
            </Button>
          </>
        }
      >
        <Typography sx={{ color: 'var(--c-texto)', fontSize: 14, mb: 2 }}>
          El turno de <strong>{form.paciente_nombre || 'este paciente'}</strong> quedará como cancelado.
        </Typography>
        <TextField
          fullWidth
          label="Motivo (opcional)"
          value={motivoCancelacion}
          onChange={e => setMotivoCancelacion(e.target.value)}
          placeholder="Ej: paciente llamó para cancelar"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' } },
            '& .MuiInputLabel-root': { color: '#90a4ae' },
            '& .MuiInputBase-input': { color: '#e0e0e0', fontSize: 13 },
          }}
        />
      </DialogShell>

      {/* ── Confirmación eliminar ── */}
      <DialogShell
        open={confirmarEliminar}
        onClose={() => setConfirmarEliminar(false)}
        maxWidth="xs"
        title="Eliminar turno"
        actions={
          <>
            <Button onClick={() => setConfirmarEliminar(false)} sx={{ color: '#90a4ae' }}>Cancelar</Button>
            <Button variant="contained"
              onClick={() => { setConfirmarEliminar(false); eliminar(); }}
              sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}>
              Eliminar
            </Button>
          </>
        }
      >
        <Typography sx={{ color: 'var(--c-texto)', fontSize: 14 }}>
          ¿Confirmás que querés eliminar este turno? Esta acción no se puede deshacer.
        </Typography>
      </DialogShell>

      {/* ── Dialog principal ── */}
      <DialogShell
        open={open}
        onClose={onClose}
        maxWidth="md"
        title={modoEdicion ? 'Gestionar turno' : 'Nuevo turno'}
        navigation={modoEdicion ? (
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{
            '& .MuiTab-root': { color: '#546e7a', textTransform: 'none', fontSize: 13 },
            '& .Mui-selected': { color: 'var(--c-acento)' },
            '& .MuiTabs-indicator': { bgcolor: 'var(--c-acento)' },
          }}>
            <Tab label="Datos del turno" />
            {mostrarRecepcionCoseguro && <Tab label="Coseguro" />}
          </Tabs>
        ) : null}
        actionsStart={getActionsStart()}
        actions={getActions()}
      >
        {error && (
          <Alert severity="error" onClose={() => setError('')}
            sx={{ mb: 2, bgcolor: 'rgba(211,47,47,0.1)', color: '#ef9a9a', '& .MuiAlert-icon': { color: '#ef9a9a' } }}>
            {error}
          </Alert>
        )}

        {modoEdicion ? (
          <>
            {activeTab === 0 && (
              <TabDatos form={form} setForm={setForm} medicos={medicos} obrasSociales={obrasSociales}
                buscandoDni={buscandoDni} estadoDni={estadoDni} setEstadoDni={setEstadoDni}
                buscarPorDni={buscarPorDni} readOnly={readOnly} rol={rolActual}
                onCrearPaciente={() => setShowCrearPaciente(true)} />
            )}
            {activeTab === 1 && mostrarRecepcionCoseguro && (
              <TabCoseguro form={form} setForm={setForm}
                ejecutarCoseguro={ejecutarCoseguro} corregirCoseguro={corregirCoseguro}
                anularCoseguro={anularCoseguro}
                puedeEditarCoseguro={puedeEditarCoseguro} esAdmin={esAdmin}
                turnoId={turnoId} />
            )}
          </>
        ) : turnoGuardado ? renderSuccess() : (
          <>
            <Stepper activeStep={activeStep} sx={{
              mb: 3,
              '& .MuiStepLabel-label': { color: '#546e7a', fontSize: 13 },
              '& .MuiStepLabel-label.Mui-active': { color: 'var(--c-acento)' },
              '& .MuiStepLabel-label.Mui-completed': { color: '#66bb6a' },
              '& .MuiStepIcon-root': { color: '#2a3652' },
              '& .MuiStepIcon-root.Mui-active': { color: '#1e56a0' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#2e7d32' },
            }}>
              <Step><StepLabel>Turno</StepLabel></Step>
              <Step><StepLabel>Paciente</StepLabel></Step>
              <Step><StepLabel>Confirmar</StepLabel></Step>
            </Stepper>
            {activeStep === 0 && renderStep0()}
            {activeStep === 1 && renderStep1()}
            {activeStep === 2 && renderStep2()}
          </>
        )}
      </DialogShell>

      <CrearPacienteDialog
        open={showCrearPaciente}
        onClose={() => setShowCrearPaciente(false)}
        dniInicial={form.paciente_dni}
        onSuccess={datos => {
          setShowCrearPaciente(false);
          poblarPacienteDesdeCreacion(datos);
        }}
      />
    </>
  );
}
